import * as v from 'valibot';

/**
 * The four operational controls: budgets, pauses, the stop switch, detections.
 *
 * One module because they are one idea seen from four angles — what the factory
 * is allowed to spend, what to do when it exceeds that, how to stop it, and how
 * anybody finds out. Splitting them into four files would hide the thing they
 * share: each is a *control* rather than a report, and a control that only
 * reports is the failure mode all four are written against.
 */

// ---------------------------------------------------------------- budgets

/**
 * What happens when a project reaches its ceiling.
 *
 * Two, and the difference is what being wrong costs. `warn` suits a project
 * somebody is actively developing, where a false stop wastes an afternoon.
 * `pause` suits one running unattended at three on a Sunday morning, where a
 * false continue wastes a day's budget and leaves half a migration behind.
 */
export const BudgetAction = v.picklist(['warn', 'pause']);
export type BudgetAction = v.InferOutput<typeof BudgetAction>;

export const ProjectBudget = v.object({
  workflowId: v.string(),
  /** Rolling 24 hours, in cents. The same window the deployment ceiling uses. */
  dailyCents: v.pipe(v.number(), v.integer(), v.minValue(1)),
  action: v.optional(BudgetAction, 'warn'),
  /**
   * Multiple of the trailing daily average that counts as an anomaly.
   *
   * Per project rather than global: a workflow that runs twice a week is
   * legitimately spiky, and one that runs hourly is not. A single global
   * multiple would either alarm on the first or never alarm on the second.
   */
  anomalyFactor: v.optional(v.pipe(v.number(), v.minValue(1.5), v.maxValue(100)), 4),
  updatedBy: v.optional(v.nullable(v.string()), null),
  updatedAt: v.optional(v.string()),
});
export type ProjectBudget = v.InferOutput<typeof ProjectBudget>;

/**
 * A project's spend against its ceiling, as the studio reads it.
 *
 * `trailingDailyCents` is what makes the number legible: £4 spent means nothing
 * until you know whether this project normally spends 40p or £40.
 */
export const ProjectSpend = v.object({
  workflowId: v.string(),
  windowHours: v.number(),
  spendCents: v.number(),
  requests: v.pipe(v.number(), v.integer()),
  /** Mean daily spend over the trailing window, excluding today. Null before there is a history. */
  trailingDailyCents: v.nullable(v.number()),
  budget: v.nullable(ProjectBudget),
  /** Spend over ceiling, 0–1+, or null when no ceiling is set. */
  utilisation: v.nullable(v.number()),
  /** True when spend is over `anomalyFactor` times the trailing average. */
  anomalous: v.boolean(),
  paused: v.boolean(),
});
export type ProjectSpend = v.InferOutput<typeof ProjectSpend>;

export const ProjectPause = v.object({
  id: v.string(),
  workflowId: v.string(),
  reason: v.string(),
  /** `budget` when the ceiling did it, `operator` when a person did. */
  source: v.optional(v.picklist(['budget', 'operator']), 'budget'),
  spendCents: v.optional(v.number(), 0),
  engagedBy: v.nullable(v.string()),
  engagedAt: v.string(),
  releasedBy: v.optional(v.nullable(v.string()), null),
  releasedAt: v.optional(v.nullable(v.string()), null),
});
export type ProjectPause = v.InferOutput<typeof ProjectPause>;

// ------------------------------------------------------------ stop switch

/**
 * The factory's stop switch.
 *
 * `scope` is a workflow id when one project was stopped and null for the whole
 * deployment. Both are the same mechanism deliberately: an operator reaching
 * for this at three in the morning should not have to work out which of two
 * controls they want, and the narrow one is what stops them reaching for the
 * broad one out of doubt.
 *
 * A reason is required. An engaged switch with no reason is one nobody dares
 * release, which is how a stopped factory stays stopped for a week.
 */
export const StopSwitch = v.object({
  id: v.string(),
  scope: v.nullable(v.string()),
  reason: v.pipe(v.string(), v.minLength(3)),
  engagedBy: v.nullable(v.string()),
  engagedAt: v.string(),
  releasedBy: v.optional(v.nullable(v.string()), null),
  releasedAt: v.optional(v.nullable(v.string()), null),
});
export type StopSwitch = v.InferOutput<typeof StopSwitch>;

/**
 * What a caller is told when the switch is on.
 *
 * The reason and the person travel with the refusal, because the first thing
 * somebody does when their run will not start is ask in a channel why — and an
 * answer that is already on their screen saves that entire exchange.
 */
export const StopVerdict = v.object({
  stopped: v.boolean(),
  scope: v.optional(v.nullable(v.string()), null),
  reason: v.optional(v.nullable(v.string()), null),
  engagedBy: v.optional(v.nullable(v.string()), null),
  engagedAt: v.optional(v.nullable(v.string()), null),
});
export type StopVerdict = v.InferOutput<typeof StopVerdict>;

// -------------------------------------------------------------- detections

export const DetectionSeverity = v.picklist(['critical', 'warning', 'info']);
export type DetectionSeverity = v.InferOutput<typeof DetectionSeverity>;

/**
 * Where a rule looks.
 *
 * Two sources, because there are two places reef records what it noticed: the
 * audit trail for what a step *did*, and the LLM ledger for what a guard
 * refused before a step could do anything.
 */
export const DetectionSource = v.picklist(['audit', 'llm']);
export type DetectionSource = v.InferOutput<typeof DetectionSource>;

export const DetectionHit = v.object({
  id: v.string(),
  rule: v.string(),
  severity: DetectionSeverity,
  /** The audit event, LLM request or run it fired on. */
  subject: v.string(),
  workflowId: v.nullable(v.string()),
  runId: v.nullable(v.string()),
  summary: v.string(),
  detail: v.optional(v.record(v.string(), v.unknown()), {}),
  detectedAt: v.string(),
  /** Null until the route actually delivered — never set optimistically. */
  notifiedAt: v.optional(v.nullable(v.string()), null),
  acknowledgedBy: v.optional(v.nullable(v.string()), null),
  acknowledgedAt: v.optional(v.nullable(v.string()), null),
});
export type DetectionHit = v.InferOutput<typeof DetectionHit>;

/**
 * Sweeps to look back over when detecting.
 *
 * Deliberately more than one tick's worth. A sweep that only read the last
 * minute would miss anything written while it was running, and the
 * `(rule, subject)` uniqueness means overlapping windows cost nothing but a
 * few rows read twice.
 */
export const DETECTION_LOOKBACK_MINUTES = 15;

/** Hits notified per sweep. Beyond this it is an incident, not a notification. */
export const MAX_NOTIFICATIONS_PER_SWEEP = 10;

// -------------------------------------------------------------- provenance

/**
 * What produced a pull request.
 *
 * Every field here is knowable only at the moment the step ran, which is why it
 * is written then. Six weeks later, "which agent wrote this and under which
 * version of its instructions" is answerable from this row and from nothing
 * else — the skill has moved on, the workflow has been edited, and the audit
 * trail records what happened without recording what governed it.
 */
export const ProvenanceRecord = v.object({
  id: v.string(),
  runId: v.string(),
  stepId: v.string(),
  workflowId: v.string(),
  repo: v.nullable(v.string()),
  pullRequest: v.nullable(v.pipe(v.number(), v.integer())),
  branch: v.optional(v.nullable(v.string()), null),
  commitSha: v.optional(v.nullable(v.string()), null),
  model: v.string(),
  /** The skill versions in force, as pinned by the step that ran. */
  skills: v.optional(v.array(v.string()), []),
  policy: v.nullable(v.string()),
  /** Connection mounts the step held, so "what could it reach" is answerable too. */
  connections: v.optional(v.array(v.string()), []),
  actorUserId: v.nullable(v.string()),
  createdAt: v.string(),
});
export type ProvenanceRecord = v.InferOutput<typeof ProvenanceRecord>;
